import { fak as fakBloom } from '@awesome.me/kit-2c0e92f331/icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import {
    ResidentCareInfoItem,
    ResidentCareInfoKey,
    ResidentCareInfoValue,
} from 'src/store/sageAdminApi';

export enum FallRisk {
    AT_RISK = 'AT_RISK',
    NOT_AT_RISK = 'NOT_AT_RISK',
}

export enum AssistLevel {
    INDEPENDENT = 'INDEPENDENT',
    ONE_PERSON_ASSIST = 'ONE_PERSON_ASSIST',
    TWO_PERSON_ASSIST = 'TWO_PERSON_ASSIST',
}

export enum LiftType {
    FULL_BODY = 'FULL_BODY',
    MECHANICAL = 'MECHANICAL',
    SIT_TO_STAND = 'SIT_TO_STAND',
}

/** NOT_AT_RISK is the safe/default state and intentionally has no label — nothing is shown for it. */
export const FallRiskLabels: Partial<Record<FallRisk, string>> = {
    [FallRisk.AT_RISK]: 'Recent Fall',
};

export const AssistLevelLabels: Record<AssistLevel, string> = {
    [AssistLevel.INDEPENDENT]: 'Independent',
    [AssistLevel.ONE_PERSON_ASSIST]: '1 Person Assist',
    [AssistLevel.TWO_PERSON_ASSIST]: '2 Person Assist',
};

export const LiftTypeLabels: Record<LiftType, string> = {
    [LiftType.FULL_BODY]: 'Full-Body Lift',
    [LiftType.MECHANICAL]: 'Mechanical Lift',
    [LiftType.SIT_TO_STAND]: 'Sit-to-Stand Lift',
};

export const FallRiskIcons: Partial<Record<FallRisk, IconDefinition>> = {
    [FallRisk.AT_RISK]: fakBloom.faFall as IconDefinition,
};

export const AssistLevelIcons: Record<AssistLevel, IconDefinition> = {
    [AssistLevel.INDEPENDENT]: fakBloom.faPerson as IconDefinition,
    [AssistLevel.ONE_PERSON_ASSIST]: fakBloom.faPersonAndHelper as IconDefinition,
    [AssistLevel.TWO_PERSON_ASSIST]: fakBloom.faTeam as IconDefinition,
};

export const LiftTypeIcons: Record<LiftType, IconDefinition> = {
    [LiftType.FULL_BODY]: fakBloom.faHoyerLift as IconDefinition,
    [LiftType.MECHANICAL]: fakBloom.faGeneralLift as IconDefinition,
    [LiftType.SIT_TO_STAND]: fakBloom.faSitstandLift as IconDefinition,
};

export interface CareInfoDisplay {
    label: string;
    /** Optional second line, rendered beneath the label in a lighter color. */
    subLabel?: string;
    icon?: IconDefinition;
}

export interface CareInfoDisplayItem extends CareInfoDisplay {
    key: string;
}

/** Shown when a value is present but unknown (null from the API). */
const UnknownIcon = fakBloom.faCircleQuestionMark as IconDefinition;

const FallRiskUnknownDisplay: CareInfoDisplay = {
    label: 'Recent Fall Unknown',
    subLabel: 'Check with Nurse',
    icon: UnknownIcon,
};

const AssistLevelUnknownDisplay: CareInfoDisplay = {
    label: 'Assist Level Unknown',
    subLabel: 'Check with Nurse',
    icon: UnknownIcon,
};

const LiftTypeUnknownDisplay: CareInfoDisplay = {
    label: 'Mechanical Lift Unknown',
    subLabel: 'Check with Nurse',
    icon: UnknownIcon,
};

export const NoCareInfoDisplay: CareInfoDisplay = {
    label: 'No Care Plan Information',
    subLabel: 'Check with Nurse',
};

/**
 * Fall risk display:
 * - AT_RISK: shown as "Recent Fall".
 * - NOT_AT_RISK: hidden — the safe state needs no callout.
 * - Unknown (null): "check with nurse".
 * - Missing (no value at all) is hidden.
 */
function getFallRiskDisplay(value: ResidentCareInfoValue): CareInfoDisplay | undefined {
    if (value === FallRisk.AT_RISK) {
        return {
            label: FallRiskLabels[FallRisk.AT_RISK]!,
            icon: FallRiskIcons[FallRisk.AT_RISK]!,
        };
    }

    if (value === FallRisk.NOT_AT_RISK) {
        return undefined;
    }

    if (value === null) {
        return FallRiskUnknownDisplay;
    }
}

/**
 * Assist level display:
 * - Independent / one / two person assist: shown with their label.
 * - Unknown (null): "check with nurse".
 * - Missing (no value at all) is hidden.
 */
function getAssistLevelDisplay(value: ResidentCareInfoValue): CareInfoDisplay | undefined {
    if (value in AssistLevelLabels) {
        const level = value as AssistLevel;
        return { label: AssistLevelLabels[level], icon: AssistLevelIcons[level] };
    }

    if (value === null) {
        return AssistLevelUnknownDisplay;
    }
}

/**
 * Lift type display:
 * - Full-body / sit-to-stand / mechanical (type unspecified): shown with their label.
 * - Unknown (null): "check with nurse".
 * - No lift (NONE, missing, or unrecognized value) is hidden.
 */
function getLiftTypeDisplay(value: ResidentCareInfoValue): CareInfoDisplay | undefined {
    if (value in LiftTypeLabels) {
        const liftType = value as LiftType;
        return { label: LiftTypeLabels[liftType], icon: LiftTypeIcons[liftType] };
    }

    if (value === null) {
        return LiftTypeUnknownDisplay;
    }
}

/** Listed in display order. */
const CareInfoResolvers: Record<
    ResidentCareInfoKey,
    (value: ResidentCareInfoValue) => CareInfoDisplay | undefined
> = {
    ASSIST_LEVEL: getAssistLevelDisplay,
    LIFT_TYPE: getLiftTypeDisplay,
    FALL_RISK: getFallRiskDisplay,
};

export const CareInfoDisplayOrder = Object.keys(CareInfoResolvers) as ResidentCareInfoKey[];

export function isAllCareInfoUnknown(items: ResidentCareInfoItem[]): boolean {
    const displayable = items.filter(({ key }) => key && key in CareInfoResolvers);
    return displayable.length > 0 && displayable.every(({ value }) => value === null);
}

export function getCareInfoDisplay(item: ResidentCareInfoItem): CareInfoDisplay | undefined {
    const { key, value } = item;
    if (!key) {
        return undefined;
    }
    return CareInfoResolvers[key]?.(value);
}
